import { FC, useCallback } from "react";
import { isEmpty } from "lodash";
import { Box, Divider, Stack, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import getImportErrorMessage, {
  ErrorCode,
} from "src/utils/Company/Absences/getImportErrorMessage";
import {
  ErrorType,
  IImportErrors,
  IImportRowErrors,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

interface ImportErrorListProps {
  importErrors: IImportErrors;
}

const ImportErrorList: FC<ImportErrorListProps> = ({ importErrors }) => {
  const renderErrorItem = useCallback(
    (
      error: IImportRowErrors,
      row: string,
      key: string,
      columnInfo?: string
    ) => (
      <div key={key} data-testid="absences-import-error-list">
        <Stack
          direction="row"
          alignItems="center"
          spacing={4}
          data-testid="absences-import-error-list__item"
        >
          <Box flexBasis="70%">
            <Typography variant="body2">
              {getImportErrorMessage(error.errorCode)}
            </Typography>
          </Box>
          <Box flexBasis="30%">
            <Typography variant="body2">
              {__("Wiersz")}: <strong>{row}</strong>
              {columnInfo && (
                <>
                  , {__("kolumna")}: <strong>{columnInfo}</strong>
                </>
              )}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ m: "0.5rem 0" }} />
      </div>
    ),
    []
  );

  return (
    <>
      <Typography variant="subtitle1" component="h3" mb="1rem">
        {__("Plik odrzucony. Błędy napotkane podczas importu:")}
      </Typography>
      {importErrors.errors?.includes(ErrorType.UNPARSEABLE_FILE) && (
        <Typography variant="body2" color="error">
          {__("Import niemożliwy, nieznany błąd.")}
        </Typography>
      )}
      {Object.entries(importErrors.rowErrors as IImportRowErrors).flatMap(
        ([row, errors]: [string, IImportRowErrors[]]) =>
          errors.flatMap((error: IImportRowErrors, errorIndex: number) => {
            // special case
            if (error.errorCode === ErrorCode.EMPLOYEE_PERSONAL_DATA_MISMATCH) {
              const customColumnInfo =
                error.columns.map((column) => column.name).join(", ") ||
                "brak danych";

              return renderErrorItem(
                error,
                row,
                `${row}-${errorIndex}`,
                customColumnInfo
              );
            }

            // If columns array is empty, render error without column info
            if (isEmpty(error.columns)) {
              return renderErrorItem(error, row, `${row}-${errorIndex}`);
            }

            // If columns array has items, render each column separately
            return error.columns.map(
              (col: { name: string }, colIndex: number) =>
                renderErrorItem(
                  error,
                  row,
                  `${row}-${errorIndex}-${colIndex}`,
                  col.name
                )
            );
          })
      )}
    </>
  );
};

export default ImportErrorList;
